import type { ComponentType, ReactNode } from 'react';
import {
  type LoaderFunction,
  Outlet,
  type ShouldRevalidateFunction,
} from 'react-router';

import { legacyHypernovaConfigValue } from '#/config/legacyHypernovaConfigUtils';
import type { HorizonAppLoaderContext } from '#/horizon/types/horizon-app-loader-context';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { STAFF_SEARCH_LOCATION_COOKIE_NAME } from '#/modules/staff/search/constants/staffSearchLocationCookieName';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonLoaderFunction } from '#/routing/types/HorizonLoaderFunction';
import type { HorizonLoaderFunctionData } from '#/routing/types/HorizonLoaderFunctionData';
import { getCookieValue } from '#/utils/cookies';

import { renderLoader } from './renderLoader/renderLoader';

export function horizonRouteToObject<
  TLoader extends HorizonLoaderFunction,
>(exportedValues: {
  id?: string;
  handle?: Record<string, unknown>;
  shouldRevalidate?: (
    // oxlint-disable-next-line typescript/no-explicit-any
    args: any,
  ) => boolean;
  loader?: TLoader;
  default?: ComponentType<HorizonAppRouteComponentProps<TLoader>>;
  hypernovaClientOnly?: boolean;
}): {
  id?: string;
  handle?: Record<string, unknown>;
  shouldRevalidate?: ShouldRevalidateFunction;
  loader: LoaderFunction;
  element: ReactNode;
} {
  const {
    id,
    handle,
    shouldRevalidate,
    loader,
    default: Component,
    hypernovaClientOnly,
  } = exportedValues;

  return {
    id,
    handle,
    shouldRevalidate,
    ...renderLoader({
      clientOnly: hypernovaClientOnly,
      loader: (args, ctx) => {
        if (!loader) return null;

        const staffSearchLocation = (() => {
          const rawValue = getCookieValue(STAFF_SEARCH_LOCATION_COOKIE_NAME);
          if (!rawValue) return null;

          try {
            return JSON.parse(rawValue) as SearchLocation;
          } catch {}

          return null;
        })();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,no-param-reassign
        args.context = {
          ...({
            type: 'hypernova',
            locale: legacyHypernovaConfigValue(
              'idealist',
              'locale',
            ) as I18nLocale,
            fetchPromises: {},
            clientIp: '',
            flash: null,
            staffSearchLocation,
            horizonServerEnv: {
              release: {
                env: 'production',
                commit: '',
                version: '',
                assetsHash: '',
                server: 'heroku',
                proxyType: 'cdn',
              },
              app: {
                url: legacyHypernovaConfigValue('idealist', 'appUrl') as string,
              },
              backend: {
                url: '',
              },
              googleMaps: null,
              mixpanel: null,
              googleTagManager: null,
              googleSiteVerification: null,
              trackJs: null,
            },
          } satisfies HorizonAppLoaderContext),
          ...args.context,
        };

        const response = loader(args, ctx);

        // Returning `undefined` causes weird issues on `react-router` such as
        // two routes showing at the same time
        if (
          response &&
          typeof response === 'object' &&
          'then' in response &&
          typeof response.then === 'function'
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
          return response.then((data: any) => data ?? null); // oxlint-disable-line typescript/no-explicit-any
        }

        return response ?? null;
      },
      render: (loaderData, params) =>
        Component ? (
          <Component
            loaderData={loaderData as HorizonLoaderFunctionData<TLoader>}
            params={params}
          />
        ) : (
          <Outlet />
        ),
    }),
  } as const;
}
